import React, { useState, useCallback, useRef } from 'react';

/**
 * Reusable chat input component.
 * Handles its own local input state and delegates submission upward.
 */
function ChatInput({ onSend, disabled, onAttachFile, attachDisabled }) {
  const [value, setValue] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const fileInputRef = useRef(null);

  const handleSubmit = useCallback(() => {
    const trimmed = value.trim();
    if ((!trimmed && !pendingFile) || disabled || attachDisabled) return;
    if (pendingFile) {
      onAttachFile?.(pendingFile);
      setPendingFile(null);
    }
    if (trimmed) {
      onSend(trimmed);
      setValue('');
    }
  }, [value, pendingFile, disabled, attachDisabled, onAttachFile, onSend]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <div className="input-area">
      <div className="input-row">
        {onAttachFile && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              accept=".csv,.doc,.docx,.pdf,.ppt,.pptx,.txt,.xls,.xlsx,.gif,.jpeg,.jpg,.png,.mp3,.mp4,.mov,.avi"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) setPendingFile(file);
              }}
            />
            <button
              type="button"
              className="attach-btn"
              aria-label="Attach file"
              title="Attach file"
              onClick={() => fileInputRef.current?.click()}
              disabled={attachDisabled}
            >
              {attachDisabled ? (
                <span className="spinner attach-spinner" role="status" aria-label="Uploading attachment" />
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
              )}
            </button>
          </>
        )}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: pendingFile ? 'flex-start' : 'center', gap: 4, minWidth: 0 }}>
          <textarea
            rows={pendingFile ? 2 : 1}
            placeholder="Write a message to Honeywell Assist"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            style={{ flex: 1, minWidth: 0, resize: 'none', border: 'none', outline: 'none', font: 'inherit', background: 'transparent' }}
          />
          {pendingFile && (
            <span className="attachment-chip" style={{ display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start', whiteSpace: 'nowrap' }}>
              {pendingFile.name}
              <button type="button" aria-label="Remove attachment" title="Remove attachment" onClick={() => setPendingFile(null)}>
                ×
              </button>
            </span>
          )}
        </div>
        <button
          className="send-btn"
          onClick={handleSubmit}
          disabled={(!value.trim() && !pendingFile) || disabled || attachDisabled}
          title="Send"
        >
          →
        </button>
      </div>
      <p className="input-disclaimer">
        Response is AI-generated. Accuracy not guaranteed. Always verify outputs and do not share personal data.
      </p>
    </div>
  );
}

export default ChatInput;
